import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Trash2, User, CalendarDays, Flag, Pencil, UserCheck } from "lucide-react";
import { PRIORITIES, PRIORITY_ORDER, dueMeta, useTeamMembers } from "./todoUtils";
import { useAuth } from "@/contexts/AuthContext";

function TodoEditForm({ t, onSave, onCancel }) {
  const [title, setTitle] = useState(t.title);
  const [priority, setPriority] = useState(t.priority || "media");
  const [due, setDue] = useState(t.due || "");
  const [assigneeId, setAssigneeId] = useState(t.assigneeId || "");
  const [saving, setSaving] = useState(false);
  const members = useTeamMembers();
  const { user } = useAuth();
  const colleagues = members.filter((m) => m.id !== user?.id);

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      await onSave({ title: title.trim(), priority, due: due || null, assigneeId: assigneeId || null });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-3 py-2.5 bg-[#FDFBF7] space-y-2 animate-fade-in-fast" style={{ boxShadow: "inset 3px 0 0 #B7955B" }} data-testid={`todo-edit-form-${t.id}`}>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onCancel(); }}
        autoFocus
        data-testid="todo-edit-title"
        className="w-full text-[13px] font-medium bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 outline-none focus:border-[#B7955B] text-slate-800"
      />
      <div className="flex items-center gap-1.5 flex-wrap">
        {PRIORITY_ORDER.map((p) => {
          const cfg = PRIORITIES[p];
          const active = priority === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => setPriority(p)}
              data-testid={`todo-edit-prio-${p}`}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full transition-all"
              style={active
                ? { background: cfg.color, color: "#fff", border: `1px solid ${cfg.color}` }
                : { background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, opacity: 0.7 }}
            >
              <Flag className="w-2.5 h-2.5" strokeWidth={2.5} /> {cfg.label}
            </button>
          );
        })}
        <input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          data-testid="todo-edit-due"
          className="text-[11px] bg-white border border-slate-200 rounded-lg px-2 py-1 outline-none text-slate-600 tabular-nums"
        />
        {colleagues.length > 0 && (
          <select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            data-testid="todo-edit-assignee"
            className="text-[11px] bg-white border border-slate-200 rounded-lg px-2 py-1 outline-none text-slate-600 max-w-[140px]"
          >
            <option value="">📌 Per me</option>
            {colleagues.map((m) => (
              <option key={m.id} value={m.id}>{m.name || m.email}</option>
            ))}
          </select>
        )}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!title.trim() || saving}
          data-testid="todo-edit-save"
          className="px-3 py-1.5 rounded-lg bg-[#0A192F] text-white text-[11.5px] font-semibold disabled:opacity-40 flex items-center gap-1.5"
        >
          <Check className="w-3 h-3" strokeWidth={3} /> Salva
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="todo-edit-cancel"
          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 text-[11.5px] font-semibold"
        >
          Annulla
        </button>
      </div>
    </div>
  );
}

export function TodoItem({ t, today, onToggle, onDelete, onUpdate, showClient = false }) {
  const [editing, setEditing] = useState(false);
  const prio = PRIORITIES[t.priority] || PRIORITIES.media;
  const due = !t.completed ? dueMeta(t.due, today) : null;

  if (editing && onUpdate) {
    return (
      <TodoEditForm
        t={t}
        onCancel={() => setEditing(false)}
        onSave={async (payload) => { await onUpdate(t, payload); setEditing(false); }}
      />
    );
  }

  return (
    <div
      className="todo-item-in group flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50/80 transition-colors"
      style={{ boxShadow: `inset 3px 0 0 ${t.completed ? "rgba(22,163,74,0.45)" : prio.color}` }}
      data-testid={`todo-item-${t.id}`}
    >
      {/* Checkbox custom animato */}
      <button
        type="button"
        onClick={() => onToggle(t)}
        data-testid={`todo-toggle-${t.id}`}
        aria-label={t.completed ? "Riapri task" : "Completa task"}
        className={`mt-0.5 w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-150 ${
          t.completed
            ? "bg-emerald-500 border-emerald-500 todo-check-on"
            : "border-slate-300 hover:border-[#B7955B] hover:shadow-[0_0_0_4px_rgba(183,149,91,0.12)]"
        }`}
      >
        {t.completed && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3.5} />}
      </button>

      {/* Contenuto */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-[13.5px] leading-snug transition-all duration-200 ${
            t.completed ? "line-through text-slate-400" : "text-slate-800 font-medium"
          }`}
        >
          {t.title}
        </p>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          {/* Priorita' */}
          {!t.completed && (
            <span
              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
              style={{ background: prio.bg, color: prio.color, border: `1px solid ${prio.border}` }}
            >
              <Flag className="w-2.5 h-2.5" strokeWidth={2.5} /> {prio.label}
            </span>
          )}
          {/* Scadenza */}
          {due && (
            <span
              className="inline-flex items-center gap-1 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full tabular-nums"
              style={{ background: due.bg, color: due.color }}
            >
              <CalendarDays className="w-2.5 h-2.5" strokeWidth={2.5} />
              {due.kind === "overdue" ? `In ritardo · ${due.label}` : due.label}
            </span>
          )}
          {/* Cliente collegato (solo vista globale) */}
          {showClient && t.clientId && (
            <Link
              to={`/clienti/${t.clientId}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 text-[10px] font-semibold hover:underline"
              style={{ color: "#B7955B" }}
              data-testid={`todo-client-${t.id}`}
            >
              <User className="w-2.5 h-2.5" strokeWidth={2.5} />
              {t.clientName || "Cliente"}
            </Link>
          )}
          {/* Assegnatario */}
          {t.assigneeId && (
            <span
              className="inline-flex items-center gap-1 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full"
              style={t.assignedToMe
                ? { background: "rgba(183,149,91,0.15)", color: "#8a6e3d", border: "1px solid rgba(183,149,91,0.35)" }
                : { background: "rgba(10,25,47,0.07)", color: "#0A192F" }}
              data-testid={`todo-assignee-${t.id}`}
            >
              <UserCheck className="w-2.5 h-2.5" strokeWidth={2.5} />
              {t.assignedToMe ? "Per te" : t.assigneeName}
            </span>
          )}
          {/* Owner (se non mio) */}
          {t.isMine === false && t.ownerName && (
            <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
              di {t.ownerName}
            </span>
          )}
        </div>
      </div>

      {/* Azioni: modifica + elimina (sempre visibili su touch, hover su desktop) */}
      <div className="flex items-center gap-0.5 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
        {onUpdate && !t.completed && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid={`todo-edit-${t.id}`}
            title="Modifica task"
            className="p-1.5 rounded-lg text-slate-400 hover:text-[#8a6e3d] hover:bg-[#F5EFE3] transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(t)}
          data-testid={`todo-del-${t.id}`}
          title="Elimina task"
          className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
